import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  Renderer2,
  ViewChild,
  computed,
  forwardRef,
  inject,
  signal,
} from '@angular/core';

import { AdvancedTooltip, Content } from '@23degrees/shared-code/models';
import { fadeInAnimation } from '@23degrees/shared/animations';
import { MessageBus } from '@23degrees/shared/bus';
import { BaseClass } from '@23degrees/shared/classes/base.class';
import { BreakPointManager } from '@23degrees/shared/manager/breakpoint/breakpoint.manager';
import { StyleService } from '@23degrees/shared/services/style';
import { TrackerService } from '@23degrees/shared/services/tracker/tracker.service';

import { MESSAGE_RECIPIENTS, REQUEST_MESSAGES, RESPONSE_MESSAGES } from '@config';
import { tooltipAnimation } from './tooltip2.animations';

import { AdvancedTooltipBase } from '@23degrees/shared-code/models';
import { EventBus } from '@23degrees/shared/bus/event/event.bus';
import { QuillRendererService } from '@23degrees/shared/services/quill-renderer/quill-renderer.service';
import { DEFAULT_TOOLTIP } from './tooltip2.constants';

import { DeltaContentDirective } from '@23degrees/shared/directives/delta-content.directive';
import { FormatPipe, ResponsiveStylePipe } from '@23degrees/shared/pipes';
import { debounceNext } from '@23degrees/utilities/debounce.utils';
import { deepValue, getTypeIndex } from '@23degrees/utilities/object.utils';
import { NgClass, NgStyle } from '@angular/common';
import { ResizeObserverDirective } from '@23degrees/directives';

interface ITooltipMessage {
  advTooltip: AdvancedTooltipBase;
  context: Record<string, any>;
  position: { x: number; y: number };
  container: HTMLElement;
  source?: 'click' | 'hover' | 'mousemove';
}

@Component({
  selector: 'tooltip2',
  templateUrl: './tooltip2.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./tooltip2.component.scss'],
  animations: [tooltipAnimation, fadeInAnimation],
  imports: [NgClass, NgStyle, FormatPipe, forwardRef(() => DeltaContentDirective), ResponsiveStylePipe, ResizeObserverDirective],
})
export class ToolTip2 extends BaseClass {
  styleService = inject(StyleService);
  private trackerService = inject(TrackerService);
  private eventBus = inject(EventBus);
  private messageBus = inject(MessageBus);
  private element = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);
  private renderer = inject(Renderer2);
  private breakPointManager = inject(BreakPointManager);
  private quillRendererService = inject(QuillRendererService);

  public visible: string = 'hidden';
  private oldTooltipType: string = '';
  public tooltipType: string = '';
  public source: 'click' | 'hover' | 'mousemove' = 'click';
  private container: HTMLElement;

  get animation() {
    const { type, style } = this.advancedTooltip();
    if (!type || type === 'classic') return 'classic';
    const animationPos = type === 'sidebar' && style.animation !== 'fade' ? `-${style.sidebar.position}` : '';
    const animationType = `${type}-${style.animation}${animationPos}`;
    return animationType;
  }

  @ViewChild('tooltipWrapper') tooltipWrapper: ElementRef;
  @ViewChild('tooltip') tooltipContainer: ElementRef;
  @ViewChild('tooltipContent') tooltipContent: ElementRef;
  @ViewChild('closeButton') closeButton: ElementRef;

  @HostBinding('style.pointerEvents') get globalPointerEvents() {
    if (this.source && this.source !== 'click') return 'none';
    return;
  }

  @Input() preFilterData: any[];
  @Input() renderData: any[];

  @Input() options: any;
  @Input() content: Content;
  @Input() displayScale: number = 1;

  @Output() changes = new EventEmitter();
  @Output() close = new EventEmitter();

  public doneAnimating: boolean = false;

  public advancedTooltip = signal<AdvancedTooltip>(undefined);

  public advStyle = computed(() => {
    const advancedTooltip = this.advancedTooltip();
    return advancedTooltip.style;
  });

  public typeClass = computed(() => {
    const advancedTooltip = this.advancedTooltip();
    return advancedTooltip.type;
  });

  public advCloseButton = computed(() => {
    const style = this.advStyle();
    return style.closeButton;
  });

  public context: any[];
  public position: any;

  public isOpen: boolean = false;
  public hasContent: boolean = true;
  public positionSet: boolean = false;
  private absoluteActive: boolean = false;
  private busLoaded: boolean = false;

  public typeStyle = signal<{ [key: string]: any }>({});
  public closeButtonStyle = {};
  public closeButtonHoverStyle = {};
  public closeButtonClasses = [];
  public hoverCloseButton: boolean = false;

  public showDefaultTooltip: boolean = false;
  private defaultTooltipDefinition = DEFAULT_TOOLTIP;

  public hasEmptyTooltipTheme = computed(() => {
    return this.advStyle().classic.theme === 'none';
  });

  ngOnChanges(changes: any) {
    if (this.content && !this.busLoaded) {
      this.busLoaded = true;
      this.messageBus.on(
        RESPONSE_MESSAGES.ContentUpdate,
        MESSAGE_RECIPIENTS.ToolTip,
        this.contentUpdate,
        this.componentDestroyed$,
        this.content.localStorage.referenceID,
      );

      this.messageBus.on('TOOLTIP_MOVE', MESSAGE_RECIPIENTS.ToolTip, this.tooltipMove, this.componentDestroyed$, this.content.localStorage.referenceID);
      this.messageBus.on('TOOLTIP_OPEN', MESSAGE_RECIPIENTS.ToolTip, this.tooltipOpen, this.componentDestroyed$, this.content.localStorage.referenceID);
      this.messageBus.on('TOOLTIP_CLOSE', MESSAGE_RECIPIENTS.ToolTip, this.tooltipClose, this.componentDestroyed$, this.content.localStorage.referenceID);
      this.messageBus.on(REQUEST_MESSAGES.ContentResize, MESSAGE_RECIPIENTS.Map, this.tooltipClose, this.componentDestroyed$);
      this.messageBus.on(RESPONSE_MESSAGES.DisplayScale, 'CATCH_ALL', this.tooltipClose, this.componentDestroyed$, this.content.localStorage.referenceID);
    }
  }

  private tooltipMove = (msg) => {
    if (!this.isOpen) return;

    if (this.tooltipType === 'classic') {
      const { position, source } = msg;
      this.source = source ?? 'click';
      if (position) {
        this.position = position;
      }

      if (this.hasContent) {
        this.setPosition(false);
      }
    }
  };

  private tooltipOpen = (msg: ITooltipMessage) => {
    const { advTooltip, context, position, container, source } = msg;
    this.source = source ?? 'click';
    if (!this.isOpen) {
      this.visible = 'hidden';
    }

    this.isOpen = true;
    this.positionSet = false;
    this.context = Array.isArray(context) ? context : [context];
    this.container = container;
    this.position = position;
    this.advancedTooltip.set(advTooltip as AdvancedTooltip);

    this.showDefaultTooltip = !this.advancedTooltip().enabled;

    this.trackClickDebounced();

    if (this.isNotEmptyDelta(this.advancedTooltip().content)) {
      this.hasContent = true;

      this.setCloseButtonStyle();
      this.cdr.detectChanges(); // TODO this is awful, tooltipWrapper does not exist when isOpen was false, rerender setTimeout?
      this.setTypeStyle();

      this.eventBus.tooltipChange.next({ slug: this.content.slug, context: { active: true, dataRef: context } });
    } else {
      this.hasContent = false;
      this.isOpen = false;
    }
    this.cdr.detectChanges();
    this.changes.emit();
  };

  public tooltipClose = (msg?) => {
    // if (!this.isOpen) return;
    this.isOpen = false;
    this.visible = 'hidden';
    this.hasContent = false;
    this.positionSet = false;
    this.doneAnimating = true;

    if (msg?.emitClose) {
      this.close.emit();
    }

    this.eventBus.tooltipChange.next({ slug: this.content.slug, context: { active: false, dataRef: null } });

    this.messageBus.emit('TOOLTIP_CLOSED', [MESSAGE_RECIPIENTS.ToolTip], this.content.localStorage.referenceID);

    this.cdr.detectChanges();
  };

  public handleClose = () => {
    this.tooltipClose();
    this.close.emit();
  };

  @debounceNext(300)
  public trackClickDebounced() {
    this.trackerService.postClickAction('tooltip_open', this.content ? this.content.slug : 'unknown');
  }

  private contentUpdate = (msg) => {
    msg.contentUpdate = true;

    if(msg?.options?.data){
      this.advancedTooltip.set(msg.options.data);
    }
    this.setTypeStyle();
    this.cdr.detectChanges();
  };

  isNotEmptyDelta(delta): boolean {
    return this.quillRendererService.deltaToString(delta).length > 1;
  }

  public tooltipContentReady() {
    if (this.tooltipType === 'classic') {
      this.setPosition(true);
    }
  }

  private setCloseButtonStyle() {
    if (!this.advCloseButton()) return;
    this.closeButtonClasses = [];
    if (this.advCloseButton().position) {
      const { horizontal, vertical } = this.advCloseButton().position;
      this.closeButtonClasses = [horizontal, vertical];
    } else {
      this.closeButtonClasses = ['top', 'right'];
    }

    this.closeButtonStyle = {
      ...this.styleService.convertFontFormat(this.advCloseButton().font),
      ...this.advCloseButton().background,
      borderRadius: `${this.advCloseButton().borderRadius}px`,
      boxShadow: this.styleService.convertShadow(this.advCloseButton().boxShadow),
      flexDirection: this.resolveFlexDirectionFromPosition(this.advCloseButton().iconPosition),
    };

    this.closeButtonHoverStyle = {
      ...this.styleService.convertFontFormat(this.advCloseButton().hoverStyle.font),
      ...this.advCloseButton().hoverStyle.background,
      borderRadius: `${this.advCloseButton().hoverStyle.borderRadius}px`,
      boxShadow: this.styleService.convertShadow(this.advCloseButton().hoverStyle.boxShadow),
      flexDirection: this.resolveFlexDirectionFromPosition(this.advCloseButton().hoverStyle.iconPosition),
    };

    if (!this.advCloseButton().text) {
      const { padding } = this.advCloseButton().font;
      const paddingLeft = `${padding.left}${padding.leftUnit}`;
      const paddingTop = `${padding.top}${padding.topUnit}`;
      const paddingBottom = `${padding.bottom}${padding.bottomUnit}`;
      const paddingRight = `${padding.right}${padding.rightUnit}`;

      this.closeButtonStyle = {
        ...this.closeButtonStyle,
        width: `calc(1em + ${paddingLeft} + ${paddingRight})`,
        height: `calc(1em + ${paddingTop} + ${paddingBottom})`,
      };
    }

    if (!this.advCloseButton().hoverStyle.text) {
      const { padding } = this.advCloseButton().hoverStyle.font;
      const paddingLeft = `${padding.left}${padding.leftUnit}`;
      const paddingTop = `${padding.top}${padding.topUnit}`;
      const paddingBottom = `${padding.bottom}${padding.bottomUnit}`;
      const paddingRight = `${padding.right}${padding.rightUnit}`;

      this.closeButtonHoverStyle = {
        ...this.closeButtonHoverStyle,
        width: `calc(1em + ${paddingLeft} + ${paddingRight})`,
        height: `calc(1em + ${paddingTop} + ${paddingBottom})`,
      };
    }
  }

  private setTypeStyle(setPosition: boolean = false) {
    if (!this.tooltipWrapper) return;
    const nativeElement = this.tooltipWrapper.nativeElement;
    const style = this.advStyle();
    this.tooltipType = this.advancedTooltip().type;

    if (this.tooltipType !== this.oldTooltipType || (this.absoluteActive && (this.source === 'click' || this.source === 'mousemove'))) {
      this.resetStyle(nativeElement);
      this.oldTooltipType = this.tooltipType;
      this.absoluteActive = false;
      this.cdr.detectChanges();
    }

    this.typeStyle.set({ ...this.advStyle().background, boxShadow: this.styleService.convertShadow(style.boxShadow) });

    if (this.tooltipType === 'classic') {
      this.setPosition(setPosition);
    } else {
      this.visible = 'visible';
      const isMobile = this.breakPointManager.isMobile(this.content.slug);

      if (this.tooltipType === 'sidebar') {
        const { width } = style.sidebar;

        const sidebarWidth = isMobile ? width.mobile : width.desktop;

        this.renderer.setStyle(nativeElement, 'width', sidebarWidth.value + sidebarWidth.unit);
        if (style.sidebar.position === 'right') {
          this.renderer.setStyle(nativeElement, 'right', '0px');
          this.renderer.setStyle(nativeElement, 'left', 'initial');
        } else {
          this.renderer.removeStyle(nativeElement, 'right');
          this.renderer.removeStyle(nativeElement, 'left');
        }
      }

      if (this.tooltipType === 'fullscreen') {
        const { width, height, content, background } = style.fullscreen;

        const fullscreenWidth = isMobile ? width.mobile : width.desktop;
        const fullscreenHeight = isMobile ? height.mobile : height.desktop;
        const contentWidth = { value: 100, unit: '%' }; // isMobile ? content.width.mobile : content.width.desktop; //you cannot change this anymore
        const contentHeight = { value: 100, unit: '%' }; // isMobile ? content.height.mobile : content.height.desktop;  //you cannot change this anymore

        this.renderer.setStyle(nativeElement, 'width', fullscreenWidth.value + fullscreenWidth.unit);
        this.renderer.setStyle(nativeElement, 'height', fullscreenHeight.value + fullscreenHeight.unit);
        this.renderer.setStyle(nativeElement, 'top', `${(100 - fullscreenHeight.value) / 2}%`);
        this.renderer.setStyle(nativeElement, 'left', `${(100 - fullscreenWidth.value) / 2}%`);
        // this.renderer.setStyle(nativeElement, 'background-color', background.backgroundColor);
        // this.renderer.setStyle(nativeElement, 'background-image', background.backgroundImage);

        this.typeStyle.update((state) => ({
          ...state,
          position: 'absolute',
          width: contentWidth.value + contentWidth.unit,
          height: contentHeight.value + contentHeight.unit,
          top: `${(100 - contentHeight.value) / 2}%`,
          left: `${(100 - contentWidth.value) / 2}%`,
        }));
      }
    }
  }

  private setPosition = (setVisible: boolean = false, source: string = 'click') => {
    if (this.tooltipType !== 'classic') {
      console.log('[tooltip2] ERROR trying to set Position on non classic tooltip');
      return;
    }

    if (!this.tooltipContainer) return;
    // console.log('setPosition', setVisible);
    const { nativeElement } = this.tooltipContainer;
    const { clientWidth, clientHeight } = nativeElement.parentElement.parentElement.parentElement as HTMLElement;
    const { maxWidth, maxHeight } = this.advStyle().classic;
    const isMobile = this.breakPointManager.isMobile(this.content.slug);
    const cuttedMaxWidth = maxWidth.mobile.value >= 93 ? { value: 93, unit: '%' } : maxWidth.mobile;
    const cuttedMaxWidthDesktop = maxWidth.desktop.value >= 93 ? { value: 93, unit: '%' } : maxWidth.desktop;
    // console.log("[SettingTooltip Width: ]",isMobile,cuttedMaxWidth);
    const tooltipWidth = isMobile ? cuttedMaxWidth : cuttedMaxWidthDesktop;
    const tooltipHeight = isMobile ? maxHeight.mobile : maxHeight.desktop;

    const parent = {
      width: clientWidth,
      height: clientHeight,
    };

    this.typeStyle.update((state) => ({
      ...state,
      maxWidth: `${(clientWidth / 100) * tooltipWidth.value}px`,
      maxHeight: `${(clientHeight / 100) * tooltipHeight.value}px`,
    }));

    this.removeAnchorClasses(this.tooltipContainer.nativeElement);

    const { width, height } = nativeElement.getBoundingClientRect();

    const tooltip = {
      width: width,
      height: height,
    };

    if (this.source === 'click' || this.source === 'mousemove' || !this.hasEmptyTooltipTheme()) {
      let anchorClasses = this.getAnchorPosition(parent, tooltip, this.position);
      // let anchorClasses = this.popupClasses(parent, tooltip, this.position);

      this.renderer.setStyle(this.tooltipWrapper.nativeElement, 'transform', `translate(${this.position.x}px, ${this.position.y}px`);

      anchorClasses = this.calcOutOfBounds(anchorClasses);
      this.renderer.addClass(this.tooltipContainer.nativeElement, anchorClasses);
    } else {
      const parentRect = nativeElement.parentElement.parentElement.parentElement.getBoundingClientRect();
      const vizRect = this.container.getBoundingClientRect();
      const bottom = Math.abs(vizRect.bottom - parentRect.bottom) + 10;

      this.renderer.setStyle(this.tooltipWrapper.nativeElement, 'transform', 'translate(-50%, 0px');
      this.renderer.setStyle(this.tooltipWrapper.nativeElement, 'left', '50%');
      this.renderer.setStyle(this.tooltipWrapper.nativeElement, 'top', 'initial');
      this.renderer.setStyle(this.tooltipWrapper.nativeElement, 'bottom', `${bottom}px`);

      this.absoluteActive = true;
    }

    this.positionSet = true;
    if (setVisible || this.showDefaultTooltip) {
      this.visible = 'visible';
    }
    this.cdr.detectChanges();
  };

  private calcOutOfBounds = (classes) => {
    /**
     * ==================================================
     * = Code Diaries
     * = Chapter: Volatile Class Members
     * ==================================================
     *
     * The object returned by getBoundingClientRect() may
     * have x and y properties in some browsers,
     * but not all.
     *
     * It always has left, top, right and bottom
     * properties, so replace x with left and y with top.
     *
     * ..to be continued
     */

    const contentRect = this.tooltipContent.nativeElement.getBoundingClientRect();
    const parentRect = this.element.nativeElement.parentElement.getBoundingClientRect();

    if (contentRect.height === 0 || this.position.x < 0 || this.position.y < 0 || this.position.x > parentRect.width || this.position.y > parentRect.height)
      return classes;

    let offsetX: number;
    let offsetY: number;
    const extraSpace = 26; // this.advancedTooltip.style.classic.theme === 'default' ? 26 : 0;
    const offsetSpace = this.closeButton ? this.closeButton.nativeElement.getBoundingClientRect().height / 2 : 0;
    const offsetSpaceWidth = this.closeButton ? this.closeButton.nativeElement.getBoundingClientRect().width / 2 : 0;
    // Check horizontal out of bounds and center it
    // TODO MAKE positioning relative not centered > makes loose pömpl

    const clickLeftZone = this.position.x < contentRect.width / 2 + offsetSpaceWidth + extraSpace;
    const clickRightZone = this.position.x > parentRect.width - contentRect.width / 2 - offsetSpaceWidth - extraSpace;

    if (
      (this.position.x - contentRect.width - offsetSpaceWidth - extraSpace < 0 && clickRightZone) ||
      (this.position.x + contentRect.width + offsetSpaceWidth + extraSpace > parentRect.width && clickLeftZone)
    ) {
      if (clickRightZone) {
        offsetX = parentRect.width - (this.position.x + contentRect.width / 2 + offsetSpaceWidth);
      }

      if (clickLeftZone) {
        offsetX = (this.position.x - contentRect.width / 2 - offsetSpaceWidth) * -1;
      }

      if (this.position.y > parentRect.height / 2) {
        classes = 'anchor-bottom';
      } else {
        classes = 'anchor-top';
      }
    }

    if (this.position.x - contentRect.width - offsetSpaceWidth < 0 && classes === 'anchor-right') {
      offsetX = (this.position.x - contentRect.width - offsetSpaceWidth) * -1;
    }

    // Check vertical out of bounds and move it in
    if (contentRect.height + this.position.y + extraSpace + offsetSpace > parentRect.height && classes === 'anchor-top') {
      offsetY = (contentRect.top + contentRect.height + extraSpace + offsetSpace - (parentRect.top + parentRect.height)) * -1;
    }

    if (parentRect.top + this.position.y - contentRect.height - extraSpace - offsetSpace < parentRect.top && classes === 'anchor-bottom') {
      offsetY = (parentRect.top + this.position.y - contentRect.height - extraSpace - offsetSpace - parentRect.top) * -1;
    }

    // check if some out of bounds values are there in transform the tooltip content
    if (!!offsetX && !!offsetY) {
      this.renderer.setStyle(this.tooltipContent.nativeElement, 'transform', `translate(calc(-50% + ${offsetX || 0}px), ${offsetY || 0}px)`);
    } else if (offsetX) {
      this.renderer.setStyle(this.tooltipContent.nativeElement, 'transform', `translateX(calc(-50% + ${offsetX || 0}px))`);
    } else if (offsetY) {
      this.renderer.setStyle(this.tooltipContent.nativeElement, 'transform', `translate(-50%, ${offsetY || 0}px)`);
    }

    return classes;
  };

  private getAnchorPosition(parent, tooltip, position) {
    let anchor = [];
    const offsetY = this.closeButton ? this.closeButton.nativeElement.getBoundingClientRect().height / 2 : 0;
    const offsetX = this.closeButton ? this.closeButton.nativeElement.getBoundingClientRect().width / 2 : 0;

    let vert = [];
    const hor = [];

    if (position.y - tooltip.height - offsetY < 0) {
      vert.push('top');
    } else if (position.y + tooltip.height + offsetY > parent.height && !(position.y + tooltip.height / 2 + offsetY < parent.height)) {
      vert.push('bottom');
    }

    // if (position.x - tooltip.width - offsetX < 0 && !( position.x - tooltip.width / 2 - offsetX > 0)) {
    if (position.x - tooltip.width - offsetX < 0 && position.x + tooltip.width + offsetX < parent.width && !(position.x - tooltip.width / 2 - offsetX > 0)) {
      hor.push('left');
    } else if (position.x + tooltip.width + offsetX > parent.width && !(position.x + tooltip.width / 2 + offsetX < parent.width)) {
      hor.push('right');
    }

    if (position.y - offsetY - tooltip.height / 2 > 0 && vert.includes('top') && hor.length > 0) {
      vert = [];
    }

    anchor = [...vert, ...hor];

    let anchorPosition = '';
    if (anchor.length === 0) {
      anchorPosition = 'bottom';
    } else {
      anchorPosition = anchor.join('-');
    }

    return `anchor-${anchorPosition}`;
  }

  // newer stuff has to be testet more
  private popupClasses(container, tooltip, pos) {
    let anchorComponents: any[];
    let anchor = '';
    const offset = this.advStyle().classic.theme === 'default' ? 0 : 0;

    if (pos.y + offset < tooltip.height) {
      anchorComponents = ['top'];
    } else if (pos.y > container.height - tooltip.height) {
      anchorComponents = ['bottom'];
    } else {
      anchorComponents = [];
    }

    if (pos.x - offset < tooltip.width / 2) {
      anchorComponents.push('left');
    } else if (pos.x > container.width - tooltip.width / 2) {
      anchorComponents.push('right');
    }

    if (anchorComponents.length === 2) {
      if (anchorComponents.includes('top') && pos.y - offset - tooltip.height / 2 > 0) anchorComponents.splice(0, 1);
      if (anchorComponents.includes('bottom') && pos.y + offset + tooltip.height / 2 < container.height) anchorComponents.splice(0, 1);
    }

    if (anchorComponents.length === 0) {
      anchor = 'bottom';
    } else {
      anchor = anchorComponents.join('-');
    }

    return `anchor-${anchor}`;
  }

  removeAnchorClasses(nativeElement) {
    this.renderer.removeStyle(this.tooltipContent.nativeElement, 'transform');
    this.renderer.removeClass(nativeElement, 'anchor-top');
    this.renderer.removeClass(nativeElement, 'anchor-top-left');
    this.renderer.removeClass(nativeElement, 'anchor-top-right');
    this.renderer.removeClass(nativeElement, 'anchor-bottom');
    this.renderer.removeClass(nativeElement, 'anchor-bottom-left');
    this.renderer.removeClass(nativeElement, 'anchor-bottom-right');
    this.renderer.removeClass(nativeElement, 'anchor-left');
    this.renderer.removeClass(nativeElement, 'anchor-right');
  }

  resetStyle(nativeElement) {
    // console.log('Reset style');
    this.renderer.removeStyle(nativeElement, 'transform');
    this.renderer.removeStyle(nativeElement, 'width');
    this.renderer.removeStyle(nativeElement, 'max-width');
    this.renderer.removeStyle(nativeElement, 'max-height');
    this.renderer.removeStyle(nativeElement, 'height');
    this.renderer.removeStyle(nativeElement, 'top');
    this.renderer.removeStyle(nativeElement, 'left');
    this.renderer.removeStyle(nativeElement, 'bottom');
    this.renderer.removeStyle(nativeElement, 'background-color');
    this.renderer.removeStyle(nativeElement, 'background-image');
    this.renderer.removeStyle(nativeElement, 'box-shadow');

    if (this.tooltipContent?.nativeElement) {
      this.renderer.removeStyle(this.tooltipContent.nativeElement, 'transform');
      this.renderer.removeStyle(this.tooltipContent.nativeElement, 'left');
      this.renderer.removeStyle(this.tooltipContent.nativeElement, 'top');
      this.renderer.removeStyle(this.tooltipContent.nativeElement, 'bottom');
    }
  }

  resolveFlexDirectionFromPosition(position) {
    switch (position) {
      case 'right':
        return 'row-reverse';
      case 'top':
        return 'column';
      case 'bottom':
        return 'column-reverse';
      default:
        break;
    }

    return null;
  }

  resized(event) {
    // console.log('[Tooltip - resize]');
    // if (this.advancedTooltip.type === 'classic') {
    //   const { padding, margin } = this.advancedTooltip.style.font;
    //   const minWidth = padding.left + padding.right + margin.left + margin.right;
    //   if (event.contentRect.width > minWidth) {
    // console.log('resized');
    this.setPosition(true);
    //   }
    // }
  }

  public animationStart = (event) => {
    // console.log('ANIMSTION start', event);
    if (event.fromState === 'void') {
      this.doneAnimating = false;
    }
  };

  public animationDone = (event) => {
    // console.log('ANIMSTION DONE', event);
    if (event.fromState === 'void') {
      this.doneAnimating = true;
    }
  };

  /**
   *
   * DEFAULT TOOLTIP FUNCTIONS AND DEFINITIONS
   *
   *
   *
   */

  // const tooltip definition for classic tooltip!
  // we need this to do basic styling and positioning
  // created with version 20
  get defaultTooltip() {
    // console.log("[getTOOLTIP]",this.content.typeSpecific.tooltip);
    return this.content.typeSpecific.tooltip;
  }

  get valueFormat() {
    if (deepValue(this.defaultTooltip, 'value.format')) return deepValue(this.defaultTooltip, 'value.format');
    return undefined;
  }

  get value(): any {
    let value: any;
    if (this.defaultTooltip.value && this.easyGetTypeValue(this.defaultTooltip.value.field, this.context) !== undefined) {
      value = this.easyGetTypeValue(this.defaultTooltip.value.field, this.context);
    }
    return value;
  }

  private easyGetTypeValue(key, item) {
    // console.log("[easyGetTypeValue]",key,item);
    if (!key) return;
    const type = key.substring(0, key.indexOf(':'));
    const fieldName = key.substring(key.indexOf(':') + 1);
    // console.log("easyGettypeValue",type,fieldName);
    return item[0][fieldName];
  }

  get name(): string {
    // console.log("[GETNAME]",this.item);
    if (this.defaultTooltip.name) {
      return this.easyGetTypeValue(this.defaultTooltip.name, this.context);
    }
    return '';
    /*
     } else if (this.labelField && deepValue(this.item, this.labelField)) {
       return deepValue(this.item, this.labelField);
     }
     */
  }

  getItemValue(item) {
    // console.log("[GetItemValue]",item)
    const val = this.easyGetTypeValue(item.field, this.context);

    if (val !== 'NA' && val !== 'N/A' && val !== null && val !== undefined) {
      return val;
    }
    return false;
  }

  getFieldFormat(item) {
    return item.format || this.content.typeSpecific.fields[getTypeIndex(item.field)].format;
  }

  getFieldType(field) {
    return this.content.typeSpecific.fields[getTypeIndex(field)].dataType;
  }

  trackByFn = (idx, item) => {
    return idx;
  };
}
